// src/components/auth/SuccessModal.jsx - Flexible Success Modal
import { useTranslation } from 'react-i18next'

const SuccessModal = ({ 
  isOpen, 
  onContinue, 
  onClose,
  title,
  message,
  email,
  target,
  primaryColor = '#8548A8',
  isLoading = false,
  showEmail = true,
  showSecurityNotice = true,
  buttonText,
  loadingText,
  icon = "checkmark", // "checkmark", "star", "heart", "custom"
  customIcon,
  variant = "default" // "default", "celebration", "verification", "registration"
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === 'rtl'
  const modalTitle = title || t('successModal.title')
  const modalMessage = message || t('successModal.message')
  const buttonLabel = buttonText || t('successModal.button')
  const loadingLabel = loadingText || t('successModal.loading')
  const securityNotice = t('successModal.securityNotice')
  // Don't render if not open
  if (!isOpen) return null

  // Get the appropriate icon based on variant and icon prop
  const getIcon = () => {
    if (customIcon) return customIcon
    
    switch (icon) {
      case "star":
        return (
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path 
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
              fill="#FFFFFF" 
            />
          </svg>
        )
      case "heart":
        return (
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path 
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" 
              fill="#FFFFFF" 
            />
          </svg>
        )
      case "checkmark":
      default:
        return (
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path 
              d="M9 12l2 2 4-4" 
              stroke="#FFFFFF" 
              strokeWidth="3" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <circle 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="#FFFFFF" 
              strokeWidth="2" 
              fill="none"
            />
          </svg>
        )
    }
  }

  // Get variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case "celebration":
        return {
          iconBg: "#FF6B6B",
          titleColor: "#1F2937",
          messageColor: "#6B7280"
        }
      case "verification":
        return {
          iconBg: "#10B981",
          titleColor: "#1F2937", 
          messageColor: "#6B7280"
        }
      case "registration":
        return {
          iconBg: "#3B82F6",
          titleColor: "#1F2937",
          messageColor: "#6B7280"
        }
      default:
        return {
          iconBg: primaryColor,
          titleColor: "#1F2937",
          messageColor: "#6B7280"
        }
    }
  }

  const variantStyles = getVariantStyles()

  return (
    <div style={{
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: '"Red Hat Display", sans-serif',
      direction: isRTL ? 'rtl' : 'ltr'
    }}
    onClick={onClose}
    >
      {/* Success Modal */}
      <div style={{
        width: '500px',
        maxWidth: '90vw',
        background: '#FFFFFF',
        borderRadius: '20px',
        padding: '40px',
        boxSizing: 'border-box',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        textAlign: 'center'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: isRTL ? 'auto' : '20px',
              left: isRTL ? '20px' : 'auto',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#F9FAFB'
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}

        {/* Success Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          background: variantStyles.iconBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px auto'
        }}>
          {getIcon()}
        </div>
        
        {/* Success Title */}
        <h2 style={{
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 600,
          fontSize: '28px',
          color: variantStyles.titleColor,
          margin: '0 0 16px 0'
        }}>
          {modalTitle}
        </h2>
        
        {/* Success Description */}
        <p style={{
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 400,
          fontSize: '16px',
          color: variantStyles.messageColor,
          margin: '0 0 32px 0',
          lineHeight: 1.5
        }}>
          {modalMessage}
        </p>
        
        {/* Email/Target Display */}
        {(showEmail && (email || target)) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '16px 20px',
            background: '#F3F4F6',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path 
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" 
                stroke="#6B7280" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <polyline 
                points="22,6 12,13 2,6" 
                stroke="#6B7280" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: '16px',
              color: '#374151',
              fontWeight: 500
            }}>
              {email || target}
            </span>
          </div>
        )}
        
        {/* Security Notice */}
        {showSecurityNotice && (
          <p style={{
            fontFamily: '"Red Hat Display", sans-serif',
            fontSize: '14px',
            color: '#6B7280',
            margin: '0 0 32px 0',
            fontStyle: 'italic'
          }}>
            {securityNotice}
          </p>
        )}
        
        {/* Continue Button */}
        <button
          onClick={onContinue}
          disabled={isLoading}
          style={{
            width: '180px',
            height: '48px',
            background: isLoading ? '#E5E7EB' : primaryColor,
            borderRadius: '12px',
            border: 'none',
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 600,
            fontSize: '16px',
            color: isLoading ? '#9CA3AF' : '#FFFFFF',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            margin: '0 auto'
          }}
          onMouseEnter={(e) => !isLoading && (e.target.style.transform = 'translateY(-1px)')}
          onMouseLeave={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
        >
          {isLoading && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{
              animation: 'spin 1s linear infinite'
            }}>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
          {isLoading ? loadingLabel : buttonLabel}
        </button>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )
}

export default SuccessModal